// floatingMenuUtils.js
// Utility functions for the floating menu

/**
 * Safely open a URL in a new tab
 * Prevents javascript: injection and only allows safe schemes
 * @param {string} url - The URL to open
 */
export function safeOpenNewTab(url) {
    if (!url) return;
    // Prevent "javascript:" injection. Only allow common safe schemes.
    const allowed = /^(https?:\/\/|mailto:|tel:)/i;
    if (!allowed.test(url)) return;
    window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Get the link range at the current cursor position
 * @param {Object} state - ProseMirror editor state
 * @returns {Object|null} Link range object with from, to, and attrs, or null
 */
export function getLinkRangeAtCursor(state) {
    const link = state.schema.marks.link;
    if (!link) return null;

    const {selection} = state;
    if (!selection.empty) return null;

    const {$from, from} = selection;

    // When the cursor is inside link text, the mark can be on the current text node.
    // When the cursor is at the end of link text, the mark can be on the previous node.
    const marksHere = state.storedMarks || $from.marks();
    let activeMark = link.isInSet(marksHere);

    let scanPos = from;

    if (!activeMark && from > 0) {
        const $prev = state.doc.resolve(from - 1);
        activeMark = link.isInSet($prev.marks());
        if (activeMark) scanPos = from - 1;
    }

    if (!activeMark) return null;

    const href = activeMark.attrs?.href || null;

    // Expand to the full contiguous link range in the parent.
    let start = scanPos;
    let end = scanPos;

    const parent = state.doc.resolve(scanPos).parent;
    const parentStart = state.doc.resolve(scanPos).start();

    // Convert scanPos into an offset within the parent.
    let offset = scanPos - parentStart;

    // Walk left within parent
    for (let i = offset; i > 0; i--) {
        const node = parent.childBefore(i).node;
        if (!node || !node.isText) break;
        const m = link.isInSet(node.marks);
        if (!m || m.attrs?.href !== href) break;
        start -= node.nodeSize;
        offset -= node.nodeSize;
    }

    // Walk right within parent
    offset = scanPos - parentStart;
    for (let i = offset; i < parent.content.size;) {
        const node = parent.childAfter(i).node;
        if (!node || !node.isText) break;
        const m = link.isInSet(node.marks);
        if (!m || m.attrs?.href !== href) break;
        end += node.nodeSize;
        i += node.nodeSize;
    }

    // `start`/`end` currently point at positions between nodes; correct end to be after content
    // Ensure start < end
    const fromPos = Math.min(start, end);
    const toPos = Math.max(start, end);

    if (fromPos === toPos) return null;

    return {from: fromPos, to: toPos, attrs: activeMark.attrs};
}

/**
 * Get expert instructions from URL parameters
 * @returns {string} Expert instructions or empty string
 */
export function getExpertInstructionsFromURL() {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return params.get('expertInstructions') || '';
}

/**
 * Update URL parameters
 * @param {Object} params - Object with key-value pairs to set in URL
 * @param {Array<string>} deleteParams - Array of parameter names to delete
 */
export function updateURLParams(params = {}, deleteParams = []) {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);

    // Delete specified parameters
    deleteParams.forEach(param => searchParams.delete(param));

    // Set new parameters
    Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
    });

    const next = `${window.location.pathname}?${searchParams.toString()}${window.location.hash || ""}`;
    window.history.replaceState(null, "", next);
    window.dispatchEvent(new PopStateEvent("popstate", {state: window.history.state}));
}
